import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    image = "image",
    links = "links",
    title = "title",
    contactType = "contactType",
    telephoneNo = "telephoneNo",
    mobileImage = "mobileImage",
}

interface LinkModelExtended {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
        image: string;
        contactType: string;
        telephoneNo: string;
        mobileImage: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.image]: string;
        [fields.links]: LinkModelExtended[];
        [fields.contactType]: "telephoneNo" | "talkExpert";
        [fields.telephoneNo]: string;
        [fields.mobileImage]: string;
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    containerWrapper?: InjectableCss;
    image?: LazyImageProps;
    linkWrapper?: InjectableCss;
    linkListStyle?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    talkExpertStyle?: InjectableCss;
    contactMobileStyle?: InjectableCss;
    desktopimageStyles?: InjectableCss;
    telephoneLinkStyles?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    containerWrapper: {
        css: css``,
    },
    telephoneLinkStyles: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 20px;
            color: rgb(38, 37, 37);

            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        line-height: 16px;
                    `,
                    css`
                        line-height: 16px;
                    `,
                    null,
                    null,
                ])};
        `,
    },
    linkListStyle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 20px;
            color: ${getProp("theme.typography.p.color")};
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        line-height: 16px;
                    `,
                    css`
                        line-height: 16px;
                    `,
                    null,
                    null,
                ])};
        `,
    },
    linkWrapper: {
        css: css`
            display: flex;
            &:nth-child(1),
            &:nth-child(3) {
                flex-basis: 100%;
            }
            &:not(:last-child) {
                padding-bottom: 8px;
            }
        `,
    },
    desktopimageStyles: {
        css: css`
            padding-right: 10px;
            align-self: center;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                ])};
        `,
    },
    titleWrapper: {
        css: css`
            padding-bottom: 16px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-bottom: 10px;
                    `,
                    css`
                        padding-bottom: 10px;
                    `,
                    null,
                    null,
                ])};
        `,
    },
    title: {
        css: css`
            font-weight: 600;
            font-size: 14px;
            line-height: 16px;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    image: {
        css: css`
            padding-right: 10px;
            display: block;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: block;
                    `,
                    css`
                        display: block;
                    `,
                    null,
                    null,
                ])};
            &.mobile-image {
                display: none;
                ${({ theme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            display: block;
                            padding-top: 0;
                            padding-right: 6px;
                        `,
                        css`
                            display: block;
                            padding-top: 0;
                            padding-right: 6px;
                        `,
                        null,
                        null,
                    ])};
            }
            img {
                width: 12px;
                height: 12px;
            }
        `,
    },
    talkExpertStyle: {
        css: css`
            color: ${getColor("text.link")};
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 20px;
            color: ${getProp("theme.typography.p.color")};

            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        line-height: 16px;
                    `,
                    css`
                        line-height: 16px;
                    `,
                    null,
                    null,
                ])};
        `,
    },
    contactMobileStyle: {
        css: css`
            display: flex;
            align-items: flex-start;
        `,
    },
};

export const FooterSalesmapQuoteList: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { links } = useGetLinks(fields.links, o => o.fields.destination);
    const styles = useMergeStyles("linkList", linkListStyles, extendedStyles);

    const alignmentStyles = {
        css: css`
            text-align: ${fields.alignment || "right"};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: flex;
                            flex-wrap: wrap;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    };

    const directionStyles = {
        css: css`
            ${fields.direction === "horizontal"
                ? "display: inline-block;"
                : "padding: 1px 0 0 0;align-items: flex-start;"};
            &[data-test-selector="telephoneNo"] {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                order: 3;
                                flex-basis: 48%;
                                background: #636161;
                                border-radius: 16px;
                                padding: 4px 16px !important;
                                align-items: center;
                                margin-top: 8px;

                                span {
                                    color: #fff !important;
                                }
                            `,
                            null,
                        ],
                        "max",
                    )}
            }
            &[data-test-selector="talkExpert"] {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                order: 4;
                                flex-basis: 48%;
                                margin-left: 8px;
                                background: #636161;
                                border-radius: 16px;
                                padding: 4px 16px !important;
                                align-items: center;
                                margin-top: 8px;

                                span {
                                    color: #fff !important;
                                }
                            `,
                            null,
                        ],
                        "max",
                    )}
            }
            ${styles.linkWrapper?.css}
        `,
    };

    return (
        <StyledWrapper {...styles.containerWrapper}>
            <StyledWrapper className="title-wrapper" {...styles.titleWrapper}>
                <Typography className="list-title" {...styles.title}>
                    {fields.title}
                </Typography>
            </StyledWrapper>
            <StyledWrapper className="list-wrapper" {...alignmentStyles}>
                {links.map((list, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper
                        data-test-selector={fields.links[index].fields.contactType}
                        className="links-wrapper"
                        key={index}
                        {...directionStyles}
                    >
                        {!fields.links[index].fields.contactType && (
                            <>
                                <LazyImage src={fields.links[index].fields.image} {...styles.image} altText="Address" />
                                <Typography as="p" data-test-selector="addressLinks" {...styles.linkListStyle}>
                                    {fields.links[index].fields.overriddenTitle || list.title}
                                </Typography>
                            </>
                        )}
                        <StyledWrapper {...styles.contactMobileStyle}>
                            {fields.links[index].fields.contactType === "telephoneNo" && (
                                <>
                                    <LazyImage src={fields.links[index].fields.image} altText="Telephone Number" {...styles.desktopimageStyles} />
                                    <LazyImage
                                        src={fields.links[index].fields.mobileImage}
                                        {...styles.image}
                                        className="mobile-image"
                                        altText="Telephone Number"
                                    />
                                    <Link
                                        data-test-selector="telephoneNoLink"
                                        href={`tel:${fields.links[index].fields.telephoneNo}`}
                                        target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                        {...styles.telephoneLinkStyles}
                                    >
                                        {fields.links[index].fields.overriddenTitle || list.title}
                                    </Link>
                                </>
                            )}
                            {fields.links[index].fields.contactType === "talkExpert" && (
                                <>
                                    <LazyImage src={fields.links[index].fields.image} {...styles.image} altText="Talk to Expert" />
                                    <LazyImage
                                        src={fields.links[index].fields.mobileImage}
                                        className="mobile-image"
                                        {...styles.image}
                                        altText="Talk to Expert"
                                    />
                                    <Link
                                        data-test-selector="talkExpertLink"
                                        href={list.url}
                                        target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                        {...styles.talkExpertStyle}
                                    >
                                        {fields.links[index].fields.overriddenTitle || list.title}
                                    </Link>
                                </>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FooterSalesmapQuoteList,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: fields.contactType,
                        displayName: "Contact Type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Telephone Number", value: "telephoneNo" },
                            { displayName: "Talk to expert", value: "talkExpert" },
                        ],
                        defaultValue: "",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        isVisible: widget => widget.fields.contactType === "talkExpert",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                    {
                        name: fields.telephoneNo,
                        displayName: "Telephone number",
                        editorTemplate: "TextField",
                        isVisible: widget => widget.fields.contactType === "telephoneNo",
                        defaultValue: "",
                    },
                    {
                        name: fields.mobileImage,
                        displayName: "Mobile Image",
                        editorTemplate: "ImagePickerField",
                        isVisible: widget =>
                            widget.fields.contactType === "talkExpert" || widget.fields.contactType === "telephoneNo",
                        defaultValue: "",
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
